/**
 * @fileoverview Sortable column header for TanStack data tables.
 *
 * Purpose: Renders a column label with a tri-state sort indicator
 * (unsorted / ascending / descending) and toggles the column's sort on click.
 *
 * Used by: the Job CRM contacts table, the POD claims schedule.
 */

import { type Column } from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { cn } from "@/utils/misc";

export function SortableHeader<TData>({
  column,
  label,
  className,
}: {
  column: Column<TData, unknown>;
  label: string;
  className?: string;
}) {
  const sort = column.getIsSorted();
  const Icon =
    sort === "asc" ? ArrowUp : sort === "desc" ? ArrowDown : ArrowUpDown;

  return (
    <button
      type="button"
      onClick={() => column.toggleSorting(sort === "asc")}
      className={cn(
        "inline-flex items-center gap-1.5 rounded px-1.5 py-1 text-xs font-semibold uppercase tracking-wider transition-colors",
        "hover:bg-muted/60 focus:outline-none focus:ring-1 focus:ring-ring",
        sort ? "text-foreground" : "text-muted-foreground",
        className,
      )}
    >
      <span>{label}</span>
      <Icon
        className={cn(
          "h-3 w-3 transition-opacity",
          sort ? "opacity-100" : "opacity-40",
        )}
        aria-hidden
      />
    </button>
  );
}
